import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Stack,
  Typography,
  Link,
} from "@mui/material";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import logo from "../../assets/logo_transparent_black.png";
import { forgotPassword } from "../../api/user";

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await forgotPassword({ email });

      if (response.data?.status === "SUCCESS") {
        setSuccess(true);
      } else {
        setError(
          response.data?.message || "Failed to send password reset email"
        );
      }
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(
        error.response?.data?.message ||
          "An error occurred while requesting password reset"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container
      sx={{
        width: "100%",
        maxWidth: "450px !important",
        height: "85vh",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
          <img
            src={logo}
            alt="Fashion Words Logo"
            style={{
              maxWidth: "150px",
              height: "auto",
              objectFit: "contain",
            }}
          />
        </Box>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: "100%",
            backgroundColor: "primary.dark",
          }}
        >
          {success ? (
            // Success state
            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  pt: 2,
                  pb: 4,
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    textAlign: "center",
                    color: "primary.light",
                    fontSize: "1.75rem",
                    fontWeight: "bold",
                    mb: 2,
                  }}
                >
                  Check Your Email
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    textAlign: "center",
                    color: "primary.light",
                    fontSize: "1rem",
                    mb: 2,
                  }}
                >
                  If an account with that email exists, we've sent a password
                  reset link to {email}.
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    color: "primary.light",
                    fontSize: "0.9rem",
                  }}
                >
                  The link will expire in 24 hours.
                </Typography>
              </Box>

              <Button
                component={RouterLink}
                to="/login"
                fullWidth
                variant="contained"
                size="large"
                sx={{
                  mt: 2,
                  py: 1.5,
                }}
              >
                Back to Login
              </Button>
            </Box>
          ) : (
            // Form state
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  pt: 2,
                  pb: 4,
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    textAlign: "center",
                    color: "primary.light",
                    fontSize: "1.75rem",
                    fontWeight: "bold",
                    mb: 2,
                  }}
                >
                  Forgot Password?
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    textAlign: "center",
                    color: "primary.light",
                    fontSize: "1rem",
                  }}
                >
                  Enter your email address and we'll send you a link to reset
                  your password.
                </Typography>
              </Box>
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    id="email"
                    label="Email Address"
                    variant="filled"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(null);
                    }}
                  />

                  {error && (
                    <Typography
                      sx={{
                        color: "primary.light",
                        fontSize: "0.875rem",
                        textAlign: "center",
                        pb: 1,
                        borderBottom: "4px solid #D0021B",
                      }}
                    >
                      {error}
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    sx={{
                      mt: 2,
                      py: 1.5,
                    }}
                  >
                    {isLoading ? "Sending..." : "Send Reset Link"}
                  </Button>

                  <Box sx={{ textAlign: "center", mt: 2 }}>
                    <Link
                      component={RouterLink}
                      to="/login"
                      sx={{
                        color: "primary.light",
                        textDecoration: "none",
                        fontSize: "0.9rem",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      Back to Login
                    </Link>
                  </Box>
                </Stack>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};
